import React from 'react';

/**
 * Paginação no cliente (renderiza apenas uma fatia da lista).
 *
 * Uso:
 *   const { pagina, setPagina, porPagina, setPorPagina } = ...
 *   const itens = lista.slice((pagina - 1) * porPagina, pagina * porPagina);
 *   <Pagination
 *     paginaAtual={pagina}
 *     totalItens={lista.length}
 *     itensPorPagina={porPagina}
 *     onPageChange={setPagina}
 *     onPageSizeChange={setPorPagina}
 *     darkMode={darkMode}
 *   />
 *
 * Props:
 *   - paginaAtual      : página atual (1-based)
 *   - totalItens       : total de itens da lista já filtrada
 *   - itensPorPagina   : tamanho da página
 *   - onPageChange     : (novaPagina) => void
 *   - onPageSizeChange : (novoTamanho) => void  (opcional; sem ele o seletor some)
 *   - darkMode         : tema escuro
 *   - opcoesPorPagina  : tamanhos oferecidos (default [10, 20, 50, 100])
 *   - rotuloItens      : nome do item no plural, para o texto ("alunos", "turmas")
 */
function Pagination({
  paginaAtual = 1,
  totalItens = 0,
  itensPorPagina = 20,
  onPageChange,
  onPageSizeChange,
  darkMode = false,
  opcoesPorPagina = [10, 20, 50, 100],
  rotuloItens = 'itens',
}) {
  const totalPaginas = Math.max(1, Math.ceil(totalItens / itensPorPagina));
  const pagina = Math.min(Math.max(1, paginaAtual), totalPaginas);

  const primeiro = totalItens === 0 ? 0 : (pagina - 1) * itensPorPagina + 1;
  const ultimo = Math.min(pagina * itensPorPagina, totalItens);

  // Janela de páginas: sempre no máximo 5 botões, centrados na página atual.
  const janela = [];
  const inicio = Math.max(1, Math.min(pagina - 2, totalPaginas - 4));
  const fim = Math.min(totalPaginas, inicio + 4);
  for (let p = inicio; p <= fim; p += 1) janela.push(p);

  const btnBase = 'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors';
  const btnIdle = darkMode
    ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
    : 'bg-slate-100 text-slate-600 hover:bg-slate-200';
  const btnAtivo = 'bg-[#0974BA] text-white';
  const btnOff = darkMode
    ? 'bg-slate-800/50 text-slate-600 cursor-not-allowed'
    : 'bg-slate-50 text-slate-300 cursor-not-allowed';
  const textoCor = darkMode ? 'text-slate-400' : 'text-slate-500';

  const irPara = (p) => {
    if (!onPageChange) return;
    const alvo = Math.min(Math.max(1, p), totalPaginas);
    if (alvo !== pagina) onPageChange(alvo);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
      {/* Contagem + itens por página */}
      <div className={`flex items-center gap-3 text-sm ${textoCor}`}>
        <span>
          {totalItens === 0
            ? `Nenhum registro`
            : `Mostrando ${primeiro}–${ultimo} de ${totalItens} ${rotuloItens}`}
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-2">
            <span className="hidden sm:inline">Por página:</span>
            <select
              value={itensPorPagina}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className={`px-2 py-1 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#0974BA]/40 ${
                darkMode
                  ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                  : 'bg-white border-slate-200 text-slate-700'
              }`}
            >
              {opcoesPorPagina.map((op) => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Navegação */}
      {totalPaginas > 1 && (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => irPara(pagina - 1)}
            disabled={pagina === 1}
            className={`${btnBase} ${pagina === 1 ? btnOff : btnIdle}`}
          >
            ‹ Anterior
          </button>

          {inicio > 1 && <span className={`px-1 ${textoCor}`}>…</span>}

          {janela.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => irPara(p)}
              className={`${btnBase} ${p === pagina ? btnAtivo : btnIdle}`}
            >
              {p}
            </button>
          ))}

          {fim < totalPaginas && <span className={`px-1 ${textoCor}`}>…</span>}

          <button
            type="button"
            onClick={() => irPara(pagina + 1)}
            disabled={pagina === totalPaginas}
            className={`${btnBase} ${pagina === totalPaginas ? btnOff : btnIdle}`}
          >
            Próxima ›
          </button>
        </div>
      )}
    </div>
  );
}

export default Pagination;
